/**
 * @file CostAlertCard.tsx
 * @brief Manage the daily platform-cost alert (threshold, recipient,
 *        enable toggle) and surface live "today vs threshold" status.
 *
 * @author DES
 * @date 26-05-2026
 * @updated 29-07-2026 — GridBlock flush layout
 * @updated 06-10-2026: copy describes the cost watch (there is no cron);
 *          "Send test alert" sends one real email (useSendTestCostAlert).
 */

import { useEffect, useMemo, useState } from "react";
import { BellRing, LoaderCircle, Save, Send, TriangleAlert } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { FormFieldGroup } from "@/components/ui/FormDialog";
import { Input } from "@/components/ui/Input";
import { Switch } from "@/components/ui/Switch";
import {
  GridBand,
  GridCell,
  GridCells,
} from "@/features/analytics/components/GridBlock";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";
import {
  useCostStatus,
  useSendTestCostAlert,
  useUpdateSettings,
} from "../api";
import { BILLING_SECTIONS } from "../billingNav";
import type { SystemSettingsResponse } from "../schemas";

function centsToDollars(stored: string | undefined): string {
  if (!stored) return "";
  const n = Number(stored);
  if (Number.isNaN(n)) return "";
  return (n / 100).toFixed(2);
}

function dollarsToCents(input: string): number {
  const n = Number(input);
  if (Number.isNaN(n)) return 0;
  return Math.round(n * 100);
}

interface CostAlertCardProps {
  data: SystemSettingsResponse | undefined;
}

export function CostAlertCard({ data }: CostAlertCardProps) {
  const settings = data?.settings ?? {};

  const [enabled, setEnabled] = useState<boolean>(false);
  const [threshold, setThreshold] = useState<string>("");
  const [email, setEmail] = useState<string>("");

  useEffect(() => {
    setEnabled(settings.COST_ALERT_ENABLED?.value === "true");
    setThreshold(centsToDollars(settings.DAILY_COST_ALERT_THRESHOLD?.value));
    setEmail(settings.COST_ALERT_EMAIL?.value ?? "");
  }, [data]);

  const update = useUpdateSettings();
  const testAlert = useSendTestCostAlert();
  const statusQuery = useCostStatus();

  const dirtyPayload = useMemo(() => {
    const patch: Record<string, string | number> = {};
    const wasEnabled = settings.COST_ALERT_ENABLED?.value === "true";
    if (enabled !== wasEnabled) {
      patch.COST_ALERT_ENABLED = enabled ? "true" : "false";
    }
    const newThreshold = dollarsToCents(threshold || "0");
    const storedThreshold = Number(
      settings.DAILY_COST_ALERT_THRESHOLD?.value ?? 0,
    );
    if (newThreshold !== storedThreshold) {
      patch.DAILY_COST_ALERT_THRESHOLD = newThreshold;
    }
    const storedEmail = settings.COST_ALERT_EMAIL?.value ?? "";
    if (email.trim() !== storedEmail) patch.COST_ALERT_EMAIL = email.trim();

    return Object.keys(patch).length > 0
      ? ({ settings: patch } as const)
      : null;
  }, [enabled, threshold, email, settings]);

  const handleSave = async () => {
    if (!dirtyPayload) return;
    try {
      await update.mutateAsync(dirtyPayload);
      toast({
        variant: "success",
        title: "Alert config saved",
        description: `Updated ${Object.keys(dirtyPayload.settings).length} setting(s).`,
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Save failed",
        description:
          err instanceof ApiClientError ? err.message : "Please try again.",
      });
    }
  };

  const handleTestAlert = async () => {
    try {
      const res = await testAlert.mutateAsync();
      toast({
        variant: res.sent ? "success" : "default",
        title: res.sent ? "Test email sent" : "No test email sent",
        description: res.sent ? `Sent to ${res.email}.` : res.reason,
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Test email failed",
        description:
          err instanceof ApiClientError ? err.message : "Please try again.",
      });
    }
  };

  const status = statusQuery.data;
  const pillVariant: "success" | "warning" | "destructive" | "outline" =
    !status?.enabled
      ? "outline"
      : status.is_critical
        ? "destructive"
        : status.is_warning
          ? "warning"
          : "success";

  return (
    <>
      <GridBand
        id={BILLING_SECTIONS.costAlerts}
        className="flex flex-wrap items-center justify-between gap-3 bg-muted/25 py-2.5"
      >
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Alerts
          </p>
          <h3 className="mt-0.5 flex items-center gap-2 text-base font-semibold tracking-tight">
            <BellRing className="h-4 w-4 text-primary" />
            Daily cost alert
          </h3>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Email an alert when today's platform cost passes the threshold.
            The cost watch checks after every recorded cost, at most every 15
            minutes, and sends one email a day.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={handleTestAlert}
            disabled={testAlert.isPending}
          >
            {testAlert.isPending ? (
              <>
                <LoaderCircle className="h-4 w-4 animate-spin" /> Sending…
              </>
            ) : (
              <>
                <Send className="h-4 w-4" /> Send test alert
              </>
            )}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!dirtyPayload || update.isPending}
          >
            {update.isPending ? (
              <>
                <LoaderCircle className="h-4 w-4 animate-spin" /> Saving…
              </>
            ) : (
              <>
                <Save className="h-4 w-4" /> Save alert config
              </>
            )}
          </Button>
        </div>
      </GridBand>

      <GridCells className="grid-cols-1 sm:grid-cols-2">
        <GridCell>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">
                Today's spend
              </p>
              <p className="mt-1 text-xl font-semibold">
                {status
                  ? `$${(status.current_cost / 100).toFixed(2)}`
                  : statusQuery.isLoading
                    ? "—"
                    : "n/a"}
                <span className="text-sm font-normal text-muted-foreground">
                  {status ? ` / $${(status.threshold / 100).toFixed(2)}` : ""}
                </span>
              </p>
              {status ? (
                <p
                  className={cn(
                    "mt-1 text-xs",
                    status.is_critical
                      ? "text-destructive"
                      : status.is_warning
                        ? "text-amber-500"
                        : "text-muted-foreground",
                  )}
                >
                  {status.percent_used.toFixed(1)}% of threshold
                </p>
              ) : null}
            </div>
            <Badge variant={pillVariant}>
              {!status?.enabled
                ? "Alerts disabled"
                : status.is_critical
                  ? "Critical"
                  : status.is_warning
                    ? "Warning"
                    : "Healthy"}
            </Badge>
          </div>
        </GridCell>

        <GridCell>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium">Enable daily emails</p>
              <p className="text-xs text-muted-foreground">
                When on, the cost watch emails the recipient once a day when
                the threshold is passed, and once per user when one person's
                spend passes the per-user limit.
              </p>
            </div>
            <Switch
              checked={enabled}
              onCheckedChange={setEnabled}
              aria-label="Toggle daily cost alert emails"
            />
          </div>
        </GridCell>
      </GridCells>

      <GridCells className="grid-cols-1 sm:grid-cols-2">
        <GridCell>
          <FormFieldGroup
            label="Daily threshold (USD)"
            htmlFor="threshold"
            hint="Stored as integer cents."
          >
            <Input
              id="threshold"
              inputMode="decimal"
              placeholder="10.00"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
            />
          </FormFieldGroup>
        </GridCell>
        <GridCell>
          <FormFieldGroup label="Alert email recipient" htmlFor="alert-email">
            <Input
              id="alert-email"
              type="email"
              placeholder="alerts@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </FormFieldGroup>
        </GridCell>
      </GridCells>

      {!enabled && !email.trim() ? (
        <GridCell>
          <div className="flex items-start gap-2 text-xs text-amber-600 dark:text-amber-400">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Alerts are disabled and no recipient is configured. Set both to
              start receiving daily reports.
            </span>
          </div>
        </GridCell>
      ) : null}
    </>
  );
}
